import { useState } from "react";
import { ChevronDown, Pencil, Plus, Trash2 } from "lucide-react";
import { inr, fmtDate } from "@/lib/api";
import { AccountsBar } from "@/components/AccountsBar";

const Btn = ({ children, ...p }) => (
  <button {...p} className="p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors">{children}</button>
);

export const GroupSection = ({ group, entries, accounts, onAdd, onEditGroup, onDeleteGroup, onEdit, onDelete, accountActions }) => {
  const [open, setOpen] = useState(true);
  const total = entries.reduce((s, e) => s + e.amount, 0);

  return (
    <section data-testid={`group-accordion-item-${group.id}`} className="bg-white border rounded-xl overflow-hidden fade-up">
      <header className="flex items-center gap-3 px-4 py-3 border-b bg-slate-50/60">
        <button data-testid={`group-toggle-${group.id}`} onClick={() => setOpen(!open)} className="flex items-center gap-2 flex-1 text-left min-w-0">
          <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? "" : "-rotate-90"}`} />
          <h3 data-testid={`group-header-${group.id}`} className="font-head text-xl font-semibold truncate uppercase tracking-wide">{group.name}</h3>
          <span className="text-xs text-slate-500 shrink-0">{entries.length} entries</span>
        </button>
        <span data-testid={`group-total-${group.id}`} className="font-mono font-bold text-emerald-700 text-sm sm:text-base">{inr(total)}</span>
        <div className="flex items-center">
          <button data-testid={`group-add-entry-${group.id}`} onClick={onAdd} disabled={!accounts.length}
            className="mr-1 inline-flex items-center gap-1 rounded-md bg-sky-600 hover:bg-sky-700 disabled:opacity-40 text-white text-xs px-2.5 py-1.5 transition-colors">
            <Plus className="w-3.5 h-3.5" /> Daily entry
          </button>
          <Btn data-testid={`group-edit-${group.id}`} onClick={onEditGroup} title="Rename"><Pencil className="w-4 h-4" /></Btn>
          <Btn data-testid={`group-delete-${group.id}`} onClick={onDeleteGroup} title="Delete"><Trash2 className="w-4 h-4" /></Btn>
        </div>
      </header>
      {open && <AccountsBar group={group} accounts={accounts} {...accountActions} />}
      {open && (
        <div className="overflow-x-auto">
          <table data-testid={`group-entries-table-${group.id}`} className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="py-2.5 px-4 font-medium">Date</th>
                <th className="py-2.5 px-4 font-medium">Site name</th>
                <th className="py-2.5 px-4 font-medium">User ID</th>
                <th className="py-2.5 px-4 font-medium text-right">Settlement amount</th>
                <th className="py-2.5 px-4 w-20" />
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-slate-400">No entries</td></tr>
              )}
              {entries.map((e) => (
                <tr key={e.id} data-testid={`entry-row-${e.id}`} className="border-t hover:bg-sky-50/40 transition-colors">
                  <td className="py-2.5 px-4 font-mono text-slate-600 whitespace-nowrap">{fmtDate(e.entry_date)}</td>
                  <td className="py-2.5 px-4">{e.site_name}</td>
                  <td className="py-2.5 px-4 font-mono">{e.user_code}</td>
                  <td data-testid={`settlement-amount-cell-${e.id}`} className="py-2.5 px-4 font-mono text-right">{inr(e.amount)}</td>
                  <td className="py-1 px-2 text-right whitespace-nowrap">
                    <Btn data-testid={`entry-edit-${e.id}`} onClick={() => onEdit(e)}><Pencil className="w-3.5 h-3.5" /></Btn>
                    <Btn data-testid={`entry-delete-${e.id}`} onClick={() => onDelete(e)}><Trash2 className="w-3.5 h-3.5" /></Btn>
                  </td>
                </tr>
              ))}
            </tbody>
            {entries.length > 0 && (
              <tfoot>
                <tr className="border-t bg-slate-50 font-semibold">
                  <td colSpan={3} className="py-2.5 px-4 text-xs uppercase tracking-wider text-slate-500">Group total</td>
                  <td className="py-2.5 px-4 font-mono text-right">{inr(total)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </section>
  );
};
